/**
 * WorkItemDetailPanel — deep-linked side panel for a single REM- work item
 * (WQ-298 / ADR-019 D2). Mirrors ThreatDetailPanel: renders via
 * `createPortal(_, document.body)` so it escapes `.admin-page-content`'s
 * stacking context (z-index:1001 > the .admin-header z-index:1000). Opens on the
 * `?work_item_id=` URL param; Escape / × closes it.
 *
 * Renders display_id (REM-NNNN), title, status/severity/lane meta, the
 * body_markdown (incl. a grouped item's member CVE table, via remark-gfm), an
 * action_url "View source" deep-link, and operator triage (status dropdown +
 * assignee). Triage PATCHes the existing WQ-294 endpoint — review-only surface,
 * no new governance logic.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { useAuth } from '../../../contexts/AuthContext';
import {
  ErrorState,
  LoadingSkeleton,
} from '../../security/components/LoadingSkeleton';
import { useWorkItemDetail } from '../hooks/useWorkItemDetail';
import { workItemsApi } from '../services/workItemsApi';
import { WORK_ITEM_STATUSES, type WorkItemStatus } from '../types/workItems';

interface WorkItemDetailPanelProps {
  /** Called after a successful status/assignee PATCH so the list can refetch. */
  onChanged?: () => void;
}

function MetaRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.8125rem', marginBottom: '0.25rem' }}>
      <span style={{ color: 'var(--text-secondary)', minWidth: '7rem' }}>{label}</span>
      <span>{value}</span>
    </div>
  );
}

export function WorkItemDetailPanel(
  { onChanged }: WorkItemDetailPanelProps,
): React.JSX.Element | null {
  const [searchParams, setSearchParams] = useSearchParams();
  const workItemId = searchParams.get('work_item_id');
  const { data, isLoading, error, refetch } = useWorkItemDetail(workItemId);
  const { authFetch } = useAuth();
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionStatus, setActionStatus] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [assignee, setAssignee] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (workItemId) panelRef.current?.focus();
  }, [workItemId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && workItemId) closePanel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workItemId]);

  if (!workItemId) return null;

  const closePanel = () => {
    setSearchParams((prev) => {
      prev.delete('work_item_id');
      return prev;
    });
    setActionError(null);
    setActionStatus(null);
    setAssignee('');
  };

  const changeStatus = async (status: WorkItemStatus) => {
    if (!data || status === data.status) return;
    setActionBusy(true);
    setActionError(null);
    setActionStatus(`Updating status → ${status}…`);
    try {
      await workItemsApi.update(authFetch, data.id, { status });
      setActionStatus(`Status updated to ${status}`);
      await refetch();
      onChanged?.();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
      setActionStatus(null);
    } finally {
      setActionBusy(false);
    }
  };

  const assign = async () => {
    if (!data || !assignee.trim()) return;
    setActionBusy(true);
    setActionError(null);
    setActionStatus('Assigning…');
    try {
      await workItemsApi.update(authFetch, data.id, {
        assigned_to_actor_id: assignee.trim(),
      });
      setActionStatus('Assignee updated');
      setAssignee('');
      await refetch();
      onChanged?.();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
      setActionStatus(null);
    } finally {
      setActionBusy(false);
    }
  };

  const panelJsx = (
    <aside
      ref={panelRef}
      role="dialog"
      aria-modal="false"
      aria-label={data ? `Work item ${data.display_id ?? data.id}` : 'Work item detail'}
      tabIndex={-1}
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: 'min(560px, 100vw)',
        background: 'var(--bg-primary, white)',
        borderLeft: '1px solid var(--border-default, #e5e7eb)',
        padding: '1rem',
        overflowY: 'auto',
        zIndex: 1001,
        boxShadow: '-4px 0 12px rgba(0,0,0,0.08)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '0.75rem',
        }}
      >
        <strong style={{ fontFamily: 'monospace', fontSize: '1rem' }}>
          {data?.display_id ?? '—'}
        </strong>
        <button className="btn btn-ghost" onClick={closePanel} aria-label="Close detail panel">
          ×
        </button>
      </div>

      {isLoading && !data && <LoadingSkeleton rows={4} />}
      {error && <ErrorState error={error} onRetry={refetch} />}

      {data && (
        <>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>{data.title}</h3>

          <MetaRow label="Status" value={<strong>{data.status}</strong>} />
          <MetaRow label="Severity" value={data.severity_token} />
          <MetaRow label="Lane" value={data.lane} />
          {data.originating_skill && (
            <MetaRow label="Origin" value={data.originating_skill} />
          )}
          {data.source_queue && (
            <MetaRow label="Source queue" value={data.source_queue} />
          )}
          {data.gate_decision_id && (
            <MetaRow
              label="Gate decision"
              value={<code style={{ fontSize: '0.75rem' }}>{data.gate_decision_id}</code>}
            />
          )}
          {data.created_at && (
            <MetaRow label="Created" value={new Date(data.created_at).toLocaleString()} />
          )}
          {data.action_url && (
            <MetaRow
              label="Source"
              value={
                <Link to={data.action_url} onClick={closePanel}>
                  View source ↗
                </Link>
              }
            />
          )}

          <div
            style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: 'var(--bg-tertiary, #f9fafb)',
              borderRadius: 6,
              fontSize: '0.8125rem',
              lineHeight: 1.5,
            }}
            className="work-item-markdown"
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {data.body_markdown}
            </ReactMarkdown>
          </div>

          {/* Triage — operator judgment (free transition in v1). */}
          <div style={{ marginTop: '1rem', borderTop: '1px solid var(--border-default, #e5e7eb)', paddingTop: '0.75rem' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Status</span>
              <select
                value={data.status}
                disabled={actionBusy}
                onChange={(e) => changeStatus(e.target.value as WorkItemStatus)}
                aria-label="Work item status"
                style={{ padding: '0.25rem 0.5rem' }}
              >
                {WORK_ITEM_STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>

            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: 1 }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  Assignee (actor id){data.assigned_to_actor_id ? ` — current: ${data.assigned_to_actor_id.slice(0, 8)}` : ''}
                </span>
                <input
                  type="text"
                  value={assignee}
                  onChange={(e) => setAssignee(e.target.value)}
                  placeholder="actor uuid"
                  aria-label="Assignee actor id"
                  style={{ padding: '0.25rem 0.5rem' }}
                />
              </label>
              <button
                className="btn btn-secondary"
                onClick={assign}
                disabled={actionBusy || !assignee.trim()}
              >
                Assign
              </button>
            </div>

            {actionStatus && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                {actionStatus}
              </div>
            )}
            {actionError && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: 'var(--danger, #dc2626)' }}>
                {actionError}
              </div>
            )}
          </div>
        </>
      )}
    </aside>
  );

  return createPortal(panelJsx, document.body);
}
